import { Plus, Trash2 } from 'lucide-react';
import { Input } from '../../ui/Input';
import type { ReactNode } from 'react';

/** Preset tenant brand colors (hex). */
export const TENANT_COLOR_PRESETS = [
  '#FFD300',
  '#d01644',
  '#0284c7',
  '#8F00FF',
  '#059669',
  '#ea580c',
  '#6366f1',
  '#64748b',
] as const;

export function normalizeHexColor(raw: string, fallback = '#6b7280'): string {
  const s = raw.trim();
  if (!s) return fallback;
  const withHash = s.startsWith('#') ? s : `#${s}`;
  if (/^#[0-9a-fA-F]{6}$/.test(withHash)) return withHash.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(withHash)) {
    const h = withHash.slice(1);
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toLowerCase();
  }
  return withHash;
}

export function isValidHexColor(raw: string): boolean {
  const s = raw.trim();
  if (!s) return true;
  const withHash = s.startsWith('#') ? s : `#${s}`;
  return /^#[0-9a-fA-F]{3}$/.test(withHash) || /^#[0-9a-fA-F]{6}$/.test(withHash);
}

export function SectionBox({
  title,
  onAdd,
  addLabel = 'Add',
  children,
  className = '',
}: {
  title: string;
  onAdd?: () => void;
  addLabel?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-lg border border-gray-200/90 dark:border-gray-700/80 bg-gray-50/50 dark:bg-gray-900/30 overflow-hidden shadow-sm ${className}`}
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2.5 bg-white/90 dark:bg-gray-900/70 border-b border-gray-200/80 dark:border-gray-700/70">
        <span className="text-sm font-semibold tracking-wide text-gray-900 dark:text-gray-100">{title}</span>
        {onAdd ? (
          <button
            type="button"
            onClick={onAdd}
            className="flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors shrink-0"
          >
            <Plus className="w-3 h-3" /> {addLabel}
          </button>
        ) : null}
      </div>
      <div className="p-3 space-y-3">{children}</div>
    </div>
  );
}

export function FieldLabel({
  label,
  children,
  className = '',
  hint,
}: {
  label: string;
  children: ReactNode;
  className?: string;
  hint?: string;
}) {
  return (
    <label className={`space-y-1 block ${className}`}>
      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{label}</span>
      {children}
      {hint ? <p className="text-[11px] text-gray-500 dark:text-gray-400">{hint}</p> : null}
    </label>
  );
}

export function CheckboxField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2.5 text-xs text-gray-700 dark:text-gray-300 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500/40 dark:border-gray-600 dark:bg-gray-800"
      />
      {label}
    </label>
  );
}

export function ColorPickerField({
  value,
  onChange,
  placeholder = '#FFD300',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  const pickerValue = isValidHexColor(value) ? normalizeHexColor(value) : '#6b7280';
  const invalid = value.trim() !== '' && !isValidHexColor(value);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <label
          className="relative h-9 w-9 shrink-0 cursor-pointer overflow-hidden rounded-lg border border-gray-300 shadow-inner dark:border-gray-600"
          style={{ backgroundColor: pickerValue }}
          title="Pick color"
        >
          <input
            type="color"
            value={pickerValue}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        </label>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`text-xs font-mono flex-1 ${invalid ? 'border-amber-400 dark:border-amber-600' : ''}`}
          spellCheck={false}
        />
        <div
          className="h-9 w-9 shrink-0 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm"
          style={{ backgroundColor: isValidHexColor(value) && value.trim() ? normalizeHexColor(value) : 'transparent' }}
          aria-hidden
        />
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mr-0.5">Presets</span>
        {TENANT_COLOR_PRESETS.map((preset) => {
          const normalizedCurrent =
            value.trim() && isValidHexColor(value) ? normalizeHexColor(value) : '';
          const selected = normalizedCurrent === preset;
          return (
            <button
              key={preset}
              type="button"
              onClick={() => onChange(preset)}
              className={`h-6 w-6 rounded-full border-2 transition-transform hover:scale-110 ${
                selected
                  ? 'border-gray-900 dark:border-white ring-2 ring-offset-1 ring-gray-400 dark:ring-gray-500'
                  : 'border-white dark:border-gray-700 shadow-sm'
              }`}
              style={{ backgroundColor: preset }}
              title={preset}
              aria-label={`Use color ${preset}`}
            />
          );
        })}
      </div>
      {invalid ? (
        <p className="text-[11px] text-amber-600 dark:text-amber-400">Enter a valid hex color (e.g. #FFD300)</p>
      ) : null}
    </div>
  );
}

export function ItemCard({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`rounded-md border border-gray-200/90 dark:border-gray-700/80 bg-white dark:bg-gray-900/60 p-3 space-y-2 shadow-sm ${className}`}
    >
      {children}
    </div>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
  disabled,
  mono,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  mono?: boolean;
}) {
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className={`text-xs ${mono ? 'font-mono' : ''}`}
    />
  );
}

export function NumberInput({
  value,
  onChange,
  placeholder,
}: {
  value: number | string;
  onChange: (value: number) => void;
  placeholder?: string;
}) {
  return (
    <Input
      type="number"
      value={value}
      onChange={(e) => onChange(Number.parseInt(e.target.value, 10) || 0)}
      placeholder={placeholder}
      className="text-xs"
    />
  );
}

export function StringListEditor({
  items,
  onChange,
  placeholder = 'value',
  emptyHint = 'No entries — add a row or leave empty.',
}: {
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
  emptyHint?: string;
}) {
  const add = () => onChange([...items, '']);
  const update = (idx: number, value: string) => {
    const next = [...items];
    next[idx] = value;
    onChange(next);
  };
  const remove = (idx: number) => {
    const next = [...items];
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div className="space-y-1">
      {items.length === 0 && <p className="text-xs text-gray-500">{emptyHint}</p>}
      {items.map((item, idx) => (
        <div key={`str-${idx}`} className="grid grid-cols-[1fr_auto] gap-1 items-center">
          <Input
            value={item}
            onChange={(e) => update(idx, e.target.value)}
            placeholder={placeholder}
            className="text-xs !py-1 font-mono"
          />
          <RemoveButton onClick={() => remove(idx)} label="Remove entry" />
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors mt-1"
      >
        <Plus className="w-3 h-3" /> Add
      </button>
    </div>
  );
}

export function RemoveButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-red-500 hover:text-red-700 px-1"
      aria-label={label}
    >
      <Trash2 className="w-3.5 h-3.5" />
    </button>
  );
}

export function SubsectionTitle({ children }: { children: ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 pt-1 border-b border-gray-200 dark:border-gray-700 pb-1 mb-1">
      {children}
    </p>
  );
}
